import { ArrowLeft, Bell, CalendarDays, CheckCircle2, ReceiptText, WalletCards } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourse, getPublicAcademyBySlug, getStudentCourseSignups, getStudentTransactions, getTenant } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/currency';
import { formatDate, formatDateTime } from '../../utils/date';
import { getHostnameContext } from '../../utils/hostname';
import { StudentSidebar } from './StudentSidebar';

function asDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function StudentPaymentReceiptPage() {
  const { paymentId } = useParams();
  const { user, loading: authLoading, logout } = useAuth();
  const { notify } = useToast();
  const hostnameContext = useMemo(() => getHostnameContext(), []);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [academyName, setAcademyName] = useState('Student workspace');
  const [currency, setCurrency] = useState('KES');

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!user?.uid || !paymentId) return;
      try {
        const allSignups = await getStudentCourseSignups(user.uid);
        let scoped = allSignups;
        if (hostnameContext.tenantSlug) {
          const academy = await getPublicAcademyBySlug(hostnameContext.tenantSlug);
          scoped = academy ? allSignups.filter((signup) => signup.tenantId === academy.id) : [];
        }
        const tenantIds = [...new Set(scoped.map((signup) => signup.tenantId).filter(Boolean))];
        const matches = await Promise.all(tenantIds.map(async (tenantId) => {
          const tenantSignups = scoped.filter((signup) => signup.tenantId === tenantId);
          const tenant = await getTenant(tenantId);
          const records = await getStudentTransactions(tenantId, user.uid, user.email || tenantSignups.find((signup) => signup.email || signup.authEmail)?.email || '', tenantSignups.map((signup) => signup.id));
          const record = records.find((item) => item.id === paymentId && item.category === 'school_fee' && item.type !== 'invoice');
          if (!record) return null;
          const course = record.courseId ? await getCourse(tenantId, record.courseId).catch(() => null) : null;
          return { ...record, tenant, course };
        }));
        const found = matches.find(Boolean) || null;
        if (mounted) {
          setPayment(found);
          setAcademyName(found?.tenant?.name || 'My learning space');
          setCurrency(found?.tenant?.settings?.currency || 'KES');
          setLoading(false);
        }
      } catch (error) {
        console.error('Unable to load student receipt', error);
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [hostnameContext.tenantSlug, paymentId, user?.email, user?.uid]);

  async function onLogout() {
    await logout();
    notify('You have been signed out.');
  }

  if (authLoading) return <LoadingState label="Loading receipt..." />;
  if (!user) return <Navigate to="/student/login" replace />;
  const paidDate = asDate(payment?.paymentDate || payment?.createdAt);
  const createdDate = asDate(payment?.createdAt);

  return (
    <main className="student-portal student-invoice-view-page">
      <StudentSidebar academyName={academyName} onLogout={onLogout} />
      <div className="student-invoice-view-shell">
        <section className="student-invoice-view-workspace">
          <header className="student-finance-page-header-new">
            <div><span>Welcome back</span><h1>Payment receipt</h1><p>{createdDate ? formatDateTime(createdDate) : 'Student finance'}</p></div>
            <button type="button" className="student-dashboard-notification" aria-label="Notifications"><Bell size={19} strokeWidth={1.7} /><i /></button>
          </header>
          {!loading && payment ? (
            <div className="student-invoice-detail-card">
              <div className="student-invoice-detail-top"><div><span className="student-invoice-detail-icon student-invoice-detail-icon--paid"><WalletCards size={22} /></span><small>Payment receipt</small><h2>{payment.receiptNumber || `PAY-${payment.id.slice(0, 8).toUpperCase()}`}</h2></div><span className="student-finance-pill-new student-finance-pill-new--posted">Posted</span></div>
              <div className="student-invoice-detail-amount student-invoice-detail-amount--paid"><small>Amount paid</small><strong>{formatCurrency(payment.amount, currency)}</strong></div>
              <div className="student-invoice-detail-grid">
                <div><small>Course</small><strong>{payment.courseName || payment.course?.name || 'Course fee'}</strong></div>
                <div><small>Payment date</small><strong>{paidDate ? formatDate(paidDate) : '—'}</strong></div>
                <div><small>Payment method</small><strong>{String(payment.paymentMethod || 'Cash').replace(/[_-]+/g, ' ')}</strong></div>
                <div><small>Payment status</small><strong><CheckCircle2 size={15} /> {payment.status || 'posted'}</strong></div>
              </div>
              {payment.reference || payment.note ? <div className="student-invoice-detail-note"><small>{payment.reference ? 'Payment reference' : 'Note from your academy'}</small><p>{payment.reference || payment.note}</p></div> : null}
              <div className="student-invoice-detail-actions"><Link to="/student/fee-payments"><ArrowLeft size={16} /> Back to fee payments</Link><Link to="/student/invoices"><ReceiptText size={16} /> View invoices</Link></div>
            </div>
          ) : loading ? <div className="student-courses-inline-loading"><span /><p>Loading receipt...</p></div> : <div className="student-courses-empty"><WalletCards size={25} /><h2>Receipt not found</h2><p>This payment receipt is not available in your student account.</p><Link to="/student/fee-payments">Back to fee payments</Link></div>}
        </section>
        <aside className="student-invoice-view-aside"><CalendarDays size={21} /><h2>Your payment is recorded.</h2><p>Keep this receipt for your records. Your academy’s payment history is always available here.</p><Link to="/student/fee-payments">View payment history</Link></aside>
      </div>
    </main>
  );
}
